import { useMemo } from 'react';
import {
  FeedbackEvent,
  type FeedbackConfig,
  type FeedbackFormLocalization,
  type FeedbackRadioOption,
} from '@investigator/shared/types';

const mapOptionsToArray = (
  options: Record<string, string>,
): FeedbackRadioOption[] => {
  return Object.entries(options).map(([value, label]) => ({
    value,
    label,
  }));
};

/**
 * Helper function to build a FeedbackConfig from localized copy.
 * This makes the configuration reusable across different localization sources.
 *
 * @param copy - The localized feedback form copy
 * @param tracking - The tracking event enum value
 * @returns Complete FeedbackConfig ready to use
 */
const buildFeedbackConfig = (
  copy: FeedbackFormLocalization,
  tracking: FeedbackEvent,
): FeedbackConfig => {
  return {
    positive: {
      title: copy.title,
      description: copy.positive.description,
      radioOptions: mapOptionsToArray(copy.positive.options),
      textareaLabel: copy.textareaLabel,
      textareaPlaceholder: copy.textareaPlaceholder,
      dismissButton: copy.buttons.dismiss,
      submitButton: copy.buttons.submit,
      ariaLabel: copy.positive.ariaLabel,
      tooltip: copy.positive.tooltip,
      errorMessage: copy.errors.analyticsUnavailable,
      successMessage: copy.success.submitted,
      successToastTitle: copy.toasts.successTitle,
      errorToastTitle: copy.toasts.errorTitle,
    },
    negative: {
      title: copy.title,
      description: copy.negative.description,
      radioOptions: mapOptionsToArray(copy.negative.options),
      textareaLabel: copy.textareaLabel,
      textareaPlaceholder: copy.textareaPlaceholder,
      dismissButton: copy.buttons.dismiss,
      submitButton: copy.buttons.submit,
      ariaLabel: copy.negative.ariaLabel,
      tooltip: copy.negative.tooltip,
      errorMessage: copy.errors.analyticsUnavailable,
      successMessage: copy.success.submitted,
      successToastTitle: copy.toasts.successTitle,
      errorToastTitle: copy.toasts.errorTitle,
    },
    tracking,
  };
};

/**
 * Hook to build feedback form configuration from localized copy and tracking event.
 * This allows components to pass their own localization, making the feedback system
 * truly reusable across different contexts.
 *
 * @param copy - The localized feedback form copy from i18n
 * @param tracking - The tracking event enum value
 * @returns Configuration object ready to pass to FeedbackThumbs
 *
 * @example
 * ```tsx
 * const feedbackCopy = i18n.pages.entities.entityAnalysisModal.feedbackForm;
 * const config = useFeedbackForm(
 *   feedbackCopy,
 *   FeedbackEvent.AI_ANALYSIS_FEEDBACK
 * );
 * return <FeedbackThumbs config={config} trackingContext={{ ... }} />;
 * ```
 */
export const useFeedbackForm = (
  copy: FeedbackFormLocalization,
  tracking: FeedbackEvent,
): FeedbackConfig => {
  return useMemo(() => buildFeedbackConfig(copy, tracking), [copy, tracking]);
};
